import { toast } from "sonner";
import type { AppNotification } from "../types/notifications";

const TOASTED_STORAGE_KEY = "carepro.notification-toasts.v1";
const MAX_TOASTED_IDS = 200;
const LIVE_WINDOW_MS = 120_000;

const toastedIds = new Set<string>(loadToastedIds());

function loadToastedIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(TOASTED_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

function persistToastedIds(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      TOASTED_STORAGE_KEY,
      JSON.stringify(Array.from(toastedIds).slice(-MAX_TOASTED_IDS))
    );
  } catch {
    // private mode / quota
  }
}

export function isRefreshRequiredPayload(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { refreshRequired?: unknown }).refreshRequired === true
  );
}

export function wasNotificationToasted(id: string): boolean {
  return toastedIds.has(id);
}

export function markNotificationToasted(id: string): void {
  if (!id || toastedIds.has(id)) return;
  toastedIds.add(id);
  persistToastedIds();
}

export function resetNotificationToastsForTests(): void {
  toastedIds.clear();
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(TOASTED_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export function shouldToastFetchedNotification(
  item: AppNotification,
  previousIds: Set<string>,
  nowMs: number = Date.now()
): boolean {
  if (!item?._id || item.readAt) return false;
  if (item.surfaces?.toast !== true) return false;
  if (previousIds.has(item._id) || toastedIds.has(item._id)) return false;
  if (previousIds.size > 0) return true;
  const created = new Date(item.createdAt).getTime();
  return Number.isFinite(created) && nowMs - created <= LIVE_WINDOW_MS;
}

export function toastNewNotifications(
  items: AppNotification[],
  previousIds: Set<string>,
  nowMs: number = Date.now()
): AppNotification[] {
  const shown: AppNotification[] = [];
  items.forEach((item) => {
    if (!shouldToastFetchedNotification(item, previousIds, nowMs)) return;
    if (showLiveNotificationToast(item)) shown.push(item);
  });
  return shown;
}

export function findExportNotification(
  items: AppNotification[],
  exportId: string
): AppNotification | undefined {
  if (!exportId) return undefined;
  return items.find(
    (item) =>
      item.entityId === exportId &&
      (item.eventType === "ExportCompleted" ||
        item.eventType === "ExportFailed" ||
        item.templateKey === "EXPORT_COMPLETED" ||
        item.templateKey === "EXPORT_FAILED" ||
        item.entityType === "EXPORT" ||
        item._id.startsWith("local-export-"))
  );
}

export function exportNoticeRetryDelaysMs(): number[] {
  return process.env.NODE_ENV === "test" ? [0] : [0, 1500, 4000];
}

export function showLiveNotificationToast(
  notification: AppNotification
): boolean {
  if (!notification?._id) return false;
  if (notification.surfaces?.toast !== true) return false;
  if (notification.readAt) return false;
  if (toastedIds.has(notification._id)) return false;

  markNotificationToasted(notification._id);

  const duration =
    typeof notification.surfaces.toastDurationMs === "number"
      ? notification.surfaces.toastDurationMs
      : 5000;

  try {
    if (notification.priority === "CRITICAL") {
      toast.error(notification.title, {
        duration: Infinity,
        closeButton: true,
      });
    } else {
      toast.info(notification.title, { duration });
    }
  } catch {
    // fail-open: drawer still receives the row
  }
  return true;
}
